import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Trash2, X, Search, Star } from "lucide-react";

const usd = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const shortAddr = (a) => (a && a.length > 12 ? `${a.slice(0, 6)}…${a.slice(-4)}` : a || "—");

const STREAMS = [
  { key: "ca_mining", label: "CA Mining", color: "#8D1CEB" },
  { key: "bot_mining", label: "BOT (mining + hashrate)", color: "#15DCAC" },
  { key: "staking", label: "Staking", color: "#C2185B" },
  { key: "community", label: "Community", color: "#7C3AED" },
];
const ALL_STREAMS = STREAMS.map((s) => s.key);

export default function PortfolioModal({ portfolio, onClose, onSaved, onDeleted }) {
  const { user } = useAuth();
  const [name, setName] = useState(portfolio?.name || "");
  const [selected, setSelected] = useState(new Set(portfolio?.wallet_addresses || []));
  const [walletStreams, setWalletStreams] = useState(() => {
    const src = portfolio?.wallet_streams || {};
    const out = {};
    for (const a of portfolio?.wallet_addresses || []) {
      const key = String(a).toLowerCase();
      out[key] = Array.isArray(src[key]) ? src[key] : [...ALL_STREAMS];
    }
    return out;
  });
  const [showcase, setShowcase] = useState(!!portfolio?.leader_showcase);
  const [options, setOptions] = useState([]);
  const [query, setQuery] = useState("");
  const [onlySelected, setOnlySelected] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/portfolios/wallet-options").then((r) => setOptions(r.data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = options;
    if (q) list = list.filter((o) => (o.name || "").toLowerCase().includes(q) || o.address.includes(q));
    if (onlySelected) list = list.filter((o) => selected.has(o.address));
    return list;
  }, [options, query, onlySelected, selected]);

  const toggle = (addr) => {
    const adding = !selected.has(addr);
    setSelected((s) => {
      const n = new Set(s);
      if (adding) n.add(addr); else n.delete(addr);
      return n;
    });
    setWalletStreams((ws) => {
      const m = { ...ws };
      if (adding) { if (!m[addr]) m[addr] = [...ALL_STREAMS]; }
      else delete m[addr];
      return m;
    });
  };

  const toggleStream = (addr, stream) => {
    setWalletStreams((ws) => {
      const cur = ws[addr] || [...ALL_STREAMS];
      const next = cur.includes(stream) ? cur.filter((x) => x !== stream) : [...cur, stream];
      return { ...ws, [addr]: next };
    });
  };

  const selectAll = () => {
    const addrs = filtered.map((o) => o.address);
    setSelected((s) => new Set([...s, ...addrs]));
    setWalletStreams((ws) => {
      const m = { ...ws };
      for (const a of addrs) if (!m[a]) m[a] = [...ALL_STREAMS];
      return m;
    });
  };

  const deselectAll = () => { setSelected(new Set()); setWalletStreams({}); };

  const save = async () => {
    if (!name.trim()) { toast.error("Give the portfolio a name"); return; }
    setBusy(true);
    try {
      const wallet_streams = {};
      for (const a of selected) wallet_streams[a] = walletStreams[a] || [...ALL_STREAMS];
      const body = { name: name.trim(), wallet_addresses: [...selected], wallet_streams };
      const res = portfolio?.id
        ? await api.put(`/portfolios/${portfolio.id}`, body)
        : await api.post("/portfolios", body);
      if (user?.is_super_admin && showcase !== !!portfolio?.leader_showcase) {
        await api.post(`/portfolios/${res.data.id}/leader-showcase`, { enabled: showcase });
        res.data.leader_showcase = showcase;
      }
      toast.success(`Portfolio "${res.data.name}" saved — ${res.data.wallet_addresses.length} wallets`);
      onSaved(res.data);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Delete portfolio "${portfolio.name}"? The wallets themselves are untouched.`)) return;
    setBusy(true);
    try {
      await api.delete(`/portfolios/${portfolio.id}`);
      toast.success("Portfolio deleted");
      onDeleted(portfolio.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(15,18,38,0.45)", backdropFilter: "blur(4px)" }} data-testid="portfolio-modal">
      <div className="glass-strong w-full max-w-2xl max-h-[88vh] flex flex-col rounded-2xl overflow-hidden" style={{ background: "rgba(255,255,255,0.97)" }}>
        <div className="flex items-center justify-between p-5 border-b border-black/5">
          <h3 className="font-display text-xl font-semibold">{portfolio?.id ? "Edit portfolio" : "New portfolio"}</h3>
          <button onClick={onClose} className="org-icon-btn" data-testid="portfolio-modal-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3 overflow-y-auto flex-1">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Portfolio name (e.g. My wallets, Team India)" className="input-glass w-full" data-testid="portfolio-name-input" />
          {user?.is_super_admin && (
            <label className="flex items-start gap-2.5 rounded-xl px-3 py-2.5 cursor-pointer" style={{ background: "rgba(217,119,6,0.07)", border: "1px solid rgba(217,119,6,0.25)" }} data-testid="portfolio-leader-showcase-toggle">
              <input type="checkbox" checked={showcase} onChange={(e) => setShowcase(e.target.checked)} style={{ accentColor: "#B45309", width: 14, height: 14, marginTop: 3 }} data-testid="portfolio-leader-showcase-checkbox" />
              <span>
                <span className="text-sm font-semibold text-ink flex items-center gap-1.5"><Star size={13} style={{ color: "#B45309" }} /> Showcase as everyone&apos;s Leader portfolio</span>
                <span className="text-[11px] text-ink-3 block mt-0.5">Every member, leader and admin will see THIS portfolio on their &quot;Leader portfolio&quot; tab — totals and charts only, wallet names &amp; addresses stay hidden. Only one portfolio can be the showcase; ticking here removes it from any other.</span>
              </span>
            </label>
          )}
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-3" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search wallets by name or address…" className="input-glass w-full !pl-9" data-testid="portfolio-wallet-search" />
            </div>
            <span className="text-xs font-mono text-ink-2 shrink-0" data-testid="portfolio-selected-count">{selected.size} selected</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button type="button" onClick={selectAll}
              className="text-xs font-bold rounded-lg px-2.5 py-1.5" style={{ background: "rgba(64,96,255,0.10)", color: "#4060FF" }}
              data-testid="portfolio-select-all">
              Select all{query.trim() ? " (filtered)" : ""} ({filtered.length})
            </button>
            <button type="button" onClick={deselectAll} disabled={selected.size === 0}
              className="text-xs font-bold rounded-lg px-2.5 py-1.5 disabled:opacity-40" style={{ background: "rgba(15,18,38,0.06)", color: "#5A607F" }}
              data-testid="portfolio-deselect-all">
              Deselect all
            </button>
            <label className="ml-auto flex items-center gap-1.5 text-xs font-bold rounded-lg px-2.5 py-1.5 cursor-pointer select-none"
              style={onlySelected
                ? { background: "rgba(0,134,58,0.12)", color: "#00863A" }
                : { background: "rgba(15,18,38,0.05)", color: "#5A607F" }}
              title="Hide every wallet that isn't in this portfolio — no more scrolling to review your picks"
              data-testid="portfolio-only-selected-toggle">
              <input type="checkbox" checked={onlySelected} onChange={(e) => setOnlySelected(e.target.checked)}
                style={{ accentColor: "#00863A", width: 13, height: 13 }} data-testid="portfolio-only-selected-checkbox" />
              Show only selected
            </label>
          </div>
          <div className="border rounded-xl overflow-hidden" style={{ borderColor: "rgba(15,18,38,0.08)" }}>
            <div className="max-h-[320px] overflow-y-auto divide-y" style={{ divideColor: "rgba(15,18,38,0.05)" }}>
              {filtered.map((o) => {
                const isSel = selected.has(o.address);
                const streams = walletStreams[o.address] || ALL_STREAMS;
                return (
                <div key={o.address} className="px-3 py-2 hover:bg-black/[0.02]" data-testid={`portfolio-wallet-option-${o.address.slice(2, 8)}`}>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input type="checkbox" checked={isSel} onChange={() => toggle(o.address)} style={{ accentColor: "#4060FF", width: 14, height: 14 }} data-testid={`portfolio-wallet-checkbox-${o.address.slice(2, 8)}`} />
                    <span className="flex-1 min-w-0">
                      <span className="text-sm font-semibold text-ink block truncate">{o.name || "Unnamed"}</span>
                      <span className="text-[11px] font-mono text-ink-3">{shortAddr(o.address)}</span>
                    </span>
                    {o.rank && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded" style={{ background: "rgba(217,119,6,0.12)", color: "#B45309" }}>{o.rank}</span>}
                    <span className="text-[11px] font-mono text-ink-2 tabular-nums">{usd(o.mining_usdt)}</span>
                  </label>
                  {isSel && (
                    <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 mt-2 pl-7" data-testid={`portfolio-wallet-streams-${o.address.slice(2, 8)}`}>
                      <span className="text-[10px] font-bold uppercase tracking-wide text-ink-3">Include:</span>
                      {STREAMS.map((s) => (
                        <label key={s.key} className="flex items-center gap-1.5 cursor-pointer select-none" title={`Include ${s.label} revenue from this wallet`}>
                          <input type="checkbox" checked={streams.includes(s.key)} onChange={() => toggleStream(o.address, s.key)}
                            style={{ accentColor: s.color, width: 13, height: 13 }}
                            data-testid={`portfolio-stream-${s.key}-${o.address.slice(2, 8)}`} />
                          <span className="text-[11px] font-semibold" style={{ color: s.color }}>{s.label}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
                );
              })}
              {filtered.length === 0 && (
                <div className="p-4 text-center text-xs text-ink-3 font-mono" data-testid="portfolio-wallet-empty">
                  {onlySelected && selected.size === 0 ? "Nothing selected yet — untick “Show only selected”." : "No wallets match."}
                </div>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center justify-between gap-2 p-5 border-t border-black/5">
          {portfolio?.id ? (
            <button onClick={remove} disabled={busy} className="btn-ghost text-rose-600 border border-rose-200" data-testid="portfolio-delete-btn"><Trash2 size={13} /> Delete</button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="btn-secondary" data-testid="portfolio-cancel-btn">Cancel</button>
            <button onClick={save} disabled={busy} className="btn-primary" data-testid="portfolio-save-btn">{busy ? "Saving…" : "Save portfolio"}</button>
          </div>
        </div>
      </div>
    </div>
  );
}
